import React from 'react';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { ReactComponent as ExpandIcon } from 'new/components/icons/fi-sr-expand-arrows-alt.svg';
import { ReactComponent as CompressIcon } from 'new/components/icons/fi-sr-collapse-arrows-alt.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-sr-refresh.svg';
import { flowTypeSupportsLayoutToggle } from 'js/components/pipelines/FlowNodes/constants';
import type { LayoutDirection } from 'js/components/pipelines/FlowNodes/types';

export type SourcePipelineToolbarItem = {
  Icon: React.ReactNode;
  title: string;
  onClick: (...args: any[]) => void;
};

export type BuildToolbarItemsInput = {
  isPreview: boolean;
  isFullScreenCanvas: boolean;
  isEmbeddedMode: boolean;
  onClickExpandFlow?: () => void;
  onClickCollapseFlow: () => void;
  refreshPipeline: (id?: number) => void;
  flowType?: string;
  layoutDirection?: LayoutDirection;
  toggleLayoutDirection?: () => void;
};

export function buildToolbarItems(input: BuildToolbarItemsInput): SourcePipelineToolbarItem[] {
  const refreshItem: SourcePipelineToolbarItem = {
    Icon: <RefreshIcon />,
    title: 'Refresh',
    onClick: input.refreshPipeline,
  };

  if (input.isPreview) return [refreshItem];

  // Hide the expand/collapse toggle in embedded (iframe) mode — the
  // "Collapse Flow" handler would navigate the host iframe to a route
  // outside the embedded view, breaking the host app's UX.
  const showExpandCollapse =
    (!!input.onClickExpandFlow || input.isFullScreenCanvas) && !input.isEmbeddedMode;

  const items: SourcePipelineToolbarItem[] = [];
  if (showExpandCollapse) {
    items.push({
      Icon: input.isFullScreenCanvas ? <CompressIcon /> : <ExpandIcon />,
      title: input.isFullScreenCanvas ? 'Collapse Flow' : 'Expand Flow',
      onClick: input.isFullScreenCanvas
        ? input.onClickCollapseFlow
        : (input.onClickExpandFlow as () => void),
    });
  }
  items.push(refreshItem);

  // Layout direction toggle is not meaningful for RAG / API server flows —
  // those renderers ignore the direction, so the toggle is hidden there.
  const showLayoutToggle =
    !!input.toggleLayoutDirection &&
    !!input.layoutDirection &&
    flowTypeSupportsLayoutToggle(input.flowType);

  if (showLayoutToggle) {
    items.push({
      Icon:
        input.layoutDirection === 'LR' ? (
          <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
        ) : (
          <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
        ),
      title:
        input.layoutDirection === 'LR'
          ? 'Switch to Top-to-Bottom layout'
          : 'Switch to Left-to-Right layout',
      onClick: input.toggleLayoutDirection as () => void,
    });
  }

  return items;
}
